import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const programs = [
  {
    name: 'Kids Robotics',
    age: '5-7 ans',
    description: "Une introduction douce et ludique à la robotique pour éveiller la curiosité des plus petits à travers des jeux interactifs.",
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/e62999d5ca87cb2ae11e329b42f06396.png',
    borderColor: 'border-t-emerald-400',
    tagColor: 'bg-emerald-500',
  },
  {
    name: 'Néo Robotics',
    age: '8-10 ans',
    description: "Apprentissage des bases de la construction et de la programmation avec des robots simples et amusants.",
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/16bee4967de0f599f2b9b90524124300.png',
    borderColor: 'border-t-[#1B6FE3]',
    tagColor: 'bg-[#1B6FE3]',
  },
  {
    name: 'Ruby Robotics',
    age: '11-13 ans',
    description: "Découverte des langages de programmation visuels pour donner vie à des créations robotiques plus complexes.",
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/f03d1135ae423dd5c6f40271abe483d7.png',
    borderColor: 'border-t-[#F97316]',
    tagColor: 'bg-[#F97316]',
  },
  {
    name: 'Archy Robotics',
    age: '14-15 ans',
    description: "Conception de structures robotiques avancées et introduction à la mécanique et l'ingénierie.",
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/09457e63cf1ee7e6ea4c6cd6defd7fc3.png',
    borderColor: 'border-t-purple-500',
    tagColor: 'bg-purple-500',
  },
  {
    name: 'Elite Robotics',
    age: '16-17 ans',
    description: "Maîtrise approfondie de la robotique, programmation textuelle et préparation aux compétitions nationales.",
    logo: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/6850637e148d6c254be897802a8ef336.png',
    borderColor: 'border-t-red-500',
    tagColor: 'bg-red-500',
  },
];

export const ProgramsPage = () => {
  return (
    <Layout>
      <section className="bg-[#1B6FE3] py-16 md:py-24 text-center" data-testid="programs-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-block bg-white/15 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-bold text-white/90 mb-5">
              PARCOURS PÉDAGOGIQUES
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">
              Trouvez le programme idéal
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
              Des parcours adaptés à chaque âge et niveau, conçus pour éveiller la curiosité et développer les compétences techniques de manière ludique.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white" data-testid="programs-list">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6">
            {programs.map((prog, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`tk-card border-t-4 ${prog.borderColor} w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]`}
                data-testid={`program-card-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <img src={prog.logo} alt={`Logo ${prog.name}`} className="h-12 w-auto" />
                  <span className={`${prog.tagColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>{prog.age}</span>
                </div>
                <h3 className="font-heading text-xl font-extrabold text-slate-900 mb-2">{prog.name}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-5">{prog.description}</p>
                <Link to={`/programmes/${['kids','neo','ruby','archy','elite'][i]}`} className="inline-flex items-center text-[#1B6FE3] font-bold text-sm hover:text-[#F97316] transition-colors">
                  Découvrir le programme <ArrowRight size={14} className="ml-1.5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 section-light-blue" data-testid="programs-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center max-w-2xl">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
            Vous hésitez entre deux programmes ?
          </h2>
          <p className="text-slate-600 mb-6">
            Notre équipe pédagogique est là pour vous conseiller et orienter votre enfant vers le parcours le plus adapté à son profil et ses envies.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            Nous contacter pour un conseil <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ProgramsPage;
